import { CaretRightIcon } from '@panwds/icons';
import { Tooltip, useOnOutsideClick } from '@panwds/react-ui';
import { classNames, twMerge } from '@panwds/tailwindcss';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { metricsHelper, pathMatch } from '../../utils/index.js';
import { FlyOutMenu } from '../FlyOutMenu/index.js';
import { useNavigationContext } from '../NavigationContext/index.js';
import { Submenu } from '../Submenu/Submenu.js';
import { navActions } from './reducer.js';
const appearanceClassName = {
    red: 'twds-bg-red-500',
    blue: 'twds-bg-blue-600',
    teal: 'twds-bg-teal-500',
    lightBlue: 'twds-bg-blue-200'
};
const IconElement = ({ icon }) => {
    return React.createElement("div", { className: "twds-flex-shrink-0 twds-flex twds-items-center twds-w-4" }, icon);
};
export function NavItem({ badge, noActiveBorder, dataMetrics, dataTestId, icon, id, isSimpleNav = false, addClassName, disabled, dotColor, dotValue, onClick, title, url, children, active, displayArrow }) {
    const { openedNav, collapsed, navRef, dispatch } = useNavigationContext();
    const location = useLocation();
    const previousOtherWasOpen = useRef(false);
    const flyOutRef = useRef(null);
    const match = !disabled && (active ?? pathMatch(location.pathname, url));
    const currentOpen = openedNav === id;
    const subnavOpened = !!openedNav;
    const history = useHistory();
    const hasArrow = displayArrow ?? !!children;
    const otherIsOpen = subnavOpened && !currentOpen;
    const hoverTimeOut = useRef(null);
    const normalizedId = metricsHelper(id);
    const [isHovered, setIsHovered] = useState(false);
    const onMouseOverAction = useCallback(() => {
        if (collapsed) {
            dispatch({ type: navActions.setHoverTimeout, payload: undefined });
            setIsHovered(true);
            dispatch({ type: navActions.openSubNav, payload: id });
        }
    }, [collapsed]);
    const onMouseOutAction = useCallback(() => {
        if (collapsed) {
            const timeOut = 1000;
            hoverTimeOut.current = setTimeout(() => {
                setIsHovered(false);
            }, timeOut);
            dispatch({
                type: navActions.setHoverTimeout,
                payload: hoverTimeOut.current
            });
        }
    }, [collapsed]);
    const onClickAction = () => {
        if (disabled)
            return;
        onClick?.();
        if (children) {
            if (!collapsed) {
                currentOpen
                    ? dispatch({ type: navActions.closeSubNav })
                    : dispatch({ type: navActions.openSubNav, payload: id });
            }
        }
        else {
            if (url)
                history.push(url);
            subnavOpened && dispatch({ type: navActions.closeSubNav });
        }
    };
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case ' ':
            case 'Enter':
                e.preventDefault();
                collapsed ? onMouseOverAction() : onClickAction();
                break;
            case 'Escape':
                e.preventDefault();
                collapsed ? onMouseOutAction() : onClickAction();
                break;
        }
    }, []);
    useEffect(() => {
        previousOtherWasOpen.current = otherIsOpen;
    }, [otherIsOpen]);
    useEffect(() => {
        return () => {
            if (hoverTimeOut.current) {
                clearTimeout(hoverTimeOut.current);
            }
        };
    }, []);
    useEffect(() => {
        if (match && !collapsed && children)
            dispatch({ type: navActions.openSubNav, payload: id });
    }, [collapsed]);
    useEffect(() => {
        if (collapsed && currentOpen) {
            dispatch({ type: navActions.closeSubNav });
        }
    }, [collapsed]);
    useOnOutsideClick(navRef, (event) => {
        if (!currentOpen)
            return;
        // When the URL is a match, then the subnav should stay open unless clicking on the nav
        if (match && !collapsed)
            return;
        if (!navRef.current?.contains(event.target)) {
            dispatch({ type: navActions.closeSubNav });
        }
    });
    const classes = twMerge('twds-h-10 twds-flex twds-box-border twds-m-0 twds-items-center twds-relative twds-pl-5 twds-pr-4 twds-py-2 hover:twds-bg-blue-steel-800 dark:hover:twds-bg-gray-800 hover:twds-text-white dark:hover:twds-text-white', disabled ? 'twds-opacity-60 twds-cursor-not-allowed' : 'twds-cursor-pointer', 'twds-text-blue-steel-300 dark:twds-text-gray-400', 'twds-border-l-4 twds-border-solid twds-border-t-0 twds-border-b-0 twds-border-r-0', !collapsed && currentOpen && 'twds-border-b-0 twds-text-white dark:twds-text-white twds-font-semibold', match
        ? `${noActiveBorder ? 'twds-border-transparent' : 'twds-border-blue-400'} twds-text-white dark:twds-text-white`
        : 'twds-border-transparent', match ? 'twds-bg-blue-steel-800 dark:twds-bg-gray-950' : 'twds-bg-blue-steel-900 dark:twds-bg-black', addClassName);
    const tabButton = (React.createElement("button", { type: "button", id: `navigation-${id}-btn`, "aria-label": title, "aria-expanded": currentOpen, "aria-controls": `navigation-item-${normalizedId}`, "data-metrics": dataMetrics || `navigation-${normalizedId}`, "data-testid": dataTestId || `navigation-${id}-btn`, "data-url": url, className: classes, onFocus: onMouseOverAction, onMouseOver: onMouseOverAction, onMouseOut: onMouseOutAction, onBlur: onMouseOutAction, onKeyDown: handleKeyDown, ref: flyOutRef, onClick: onClickAction },
        dotColor &&
            (collapsed || subnavOpened) &&
            (!dotValue ? (React.createElement("div", { className: classNames('twds-box-border twds-absolute twds-h-3 twds-right-5 twds-top-1.5 twds-ml-1 twds-round twds-rounded-full twds-w-3', appearanceClassName[dotColor]) })) : (React.createElement("div", { className: classNames('twds-box-border twds-flex twds-justify-center twds-leading-4 twds-text-xs twds-absolute twds-h-4 twds-right-3.5 twds-top-1.5 twds-ml-1 twds-rounded twds-w-4', appearanceClassName[dotColor]) }, dotValue > 10 ? '9+' : dotValue))),
        React.createElement(IconElement, { icon: icon }),
        hasArrow && collapsed && (React.createElement(CaretRightIcon, { className: classNames('twds-absolute twds-left-11 twds-transition-opacity twds-duration-150', collapsed ? 'twds-opacity-100' : 'twds-opacity-0'), size: 'xxs' })),
        !collapsed && (React.createElement("div", { className: "twds-box-border twds-ml-6 twds-mr-3 twds-flex twds-flex-grow twds-items-center twds-justify-between twds-overflow-hidden" },
            React.createElement("span", { className: "twds-truncate" }, title),
            badge && React.createElement("div", null, badge))),
        hasArrow && !collapsed && (React.createElement(CaretRightIcon, { className: classNames('twds-absolute  twds-mr-4 twds-transition twds-duration-150', openedNav ? ' twds-left-11' : 'twds-right-0'), size: 'xxs' }))));
    return (React.createElement(Fragment, null,
        React.createElement("li", { className: "twds-flex twds-flex-col twds-min-h-0", "data-metrics": dataMetrics || `navigation-${metricsHelper(id)}`, "data-testid": dataTestId || `navigation-${id}` },
            !isSimpleNav && isHovered && collapsed && !children ? (React.createElement(Tooltip, { defaultPlacement: "right", singleLine: true, label: title }, tabButton)) : (tabButton),
            !isSimpleNav && !collapsed && children && (React.createElement(Submenu, { title: title, isOpen: currentOpen, otherWasOpen: previousOtherWasOpen.current, id: id }, children)),
            !isSimpleNav && isHovered && currentOpen && collapsed && children && (React.createElement(FlyOutMenu, { title: title, menuRef: flyOutRef, onMouseOver: onMouseOverAction, onMouseOut: onMouseOutAction }, children)))));
}
//# sourceMappingURL=NavItem.js.map